<template>
    <component
        :is="target"
        :sdk2WebTicket="sdk2WebTicket"
        :initialRevoke="initialRevoke"
        :captchaData="captchaData"
        :platform="platform"
        @on-step="handleStep"
        @on-back="handleBack"
    />
</template>

<script>
import Vue from 'vue';
import { mapState, mapMutations } from 'vuex';
import { Dialog } from 'vant';
import moment from 'moment';
import isString from 'lodash/isString';
import { sdk2WebLogin, revokeInit } from '@/api/revoke';
import { jsonParser } from '@/lib/utils';
import RevokeLoading from './components/RevokeLoading.vue';
import RevokeCondition from './components/RevokeCondition.vue';
import RevokeProtocol from './components/RevokeProtocol.vue';
import Reconfirm from './components/Reconfirm.vue';
import RevokeAccount from './components/RevokeAccount.vue';
import IOSRevalidate from './components/IOSRevalidate.vue';
import Hesitation from './components/Hesitation.vue';
import Success from './components/Success.vue';
import SuccessWithHesitate from './components/SuccessWithHesitate.vue';
import { Target } from './constant';

Vue.use(Dialog);

export default {
    name: 'Revoke',
    components: {
        RevokeLoading,
        RevokeCondition,
        RevokeProtocol,
        Reconfirm,
        RevokeAccount,
        IOSRevalidate,
        Hesitation,
        Success,
        SuccessWithHesitate,
    },
    data() {
        return {
            step: Target.RevokeLoading,
        };
    },
    computed: {
        // prettier-ignore
        ...mapState({ platform: state => state.platform || jsonParser(sessionStorage.getItem('platform')) }),
        ...mapState('revoke', [
            'sdk2WebTicket',
            'userInfo',
            'initialRevoke',
            'captchaData',
            'appInfo',
        ]),

        isInited() {
            return !!(this.appInfo.appId && this.appInfo.channel);
        },
        target: {
            set(t) {
                this.step = t;
            },
            get() {
                return Target[this.step];
            },
        },
    },
    watch: {
        sdk2WebTicket(currTicket) {
            if (/^errorId/.test(currTicket)) {
                // 获取 webTicket 失败，一直提示
                Dialog.alert({
                    title: this.$t('common.tip'),
                    message: 'webTicket is required, Please try again.',
                    confirmButtonText: this.$t('common.confirm'),
                    confirmButtonColor: 'var(--g-text-link-color)',
                    beforeClose(action, done) {
                        if (action === 'confirm') {
                            window.setTimeout(done, 800);
                        }
                    },
                }).then(() => {
                    this.setSdk2WebTicket();
                });
            } else if (isString(currTicket)) {
                // 注销流程初始化
                this.init({
                    ...this.appInfo,
                    ticket: currTicket,
                });
            }
        },
    },
    created() {
        const { appId, channel, target, platform } = this.$route.query;
        if (target && this.isInited) {
            this.setTarget(+target);
        } else {
            if (!appId) return this.$toast('appId parameter is required');
            if (!channel) return this.$toast('channel parameter is required');
            this.setAppInfo({ appId, channel });

            // h5场景直接进行注销流程
            if ([1, 2].includes(Number(platform))) {
                this.init({
                    ...this.appInfo,
                });
                return;
            }
            // 获取 sdk 票据，打通登录态
            this.setSdk2WebTicket(this.platform);
        }
    },
    methods: {
        ...mapMutations('revoke', [
            'setSdk2WebTicket',
            'setAppInfo',
            'setUserInfo',
            'setInitialRevoke',
            'setCaptchaData',
        ]),

        setTarget(target) {
            if (typeof target === 'number') this.target = target;
        },
        /**
         * @typedef {Object} StepOption
         * @property {string} current 当前组件名对应的 Target 枚举类型
         * @property {(number | undefined)} target 下一步要到达的组件
         * @property {(object | undefined)} data 当前流程所产生的数据（比如验证码流程）
         * @param {StepOption} options
         */
        handleStep({ current, target, data }) {
            if (!this.initialRevoke.flowId) return;

            switch (current) {
                case Target.RevokeAccount:
                case Target.Hesitation:
                case Target.IOSRevalidate:
                    this.setCaptchaData({
                        ...data,
                        commitShowTime: moment().format('YYYY-MM-DD HH:mm:ss'),
                    });
                    break;
                default:
                    break;
            }

            this.setTarget(target);
        },
        handleBack(current) {
            const targetToEnum = Target[current];
            let prevTarget = null;
            switch (targetToEnum) {
                case Target.RevokeProtocol:
                    prevTarget = Target.RevokeCondition;
                    break;
                case Target.Reconfirm:
                    prevTarget = Target.RevokeProtocol;
                    break;
                case Target.RevokeAccount:
                    prevTarget = Target.Reconfirm;
                    break;
                case Target.IOSRevalidate:
                    prevTarget = Target.RevokeAccount;
                    break;
                case Target.Hesitation:
                    prevTarget = Target.RevokeAccount;
                    break;
                default:
                    break;
            }
            this.setTarget(prevTarget);
        },
        async init({ appId, channel, ticket }) {
            const { platform } = this.$route.query;

            // h5场景直接进行注销流程
            if (![1, 2].includes(Number(platform))) {
                // 打通 sdk 和 web 登录态
                const userInfo = await sdk2WebLogin({
                    appId,
                    channel,
                    sdk2WebTicket: ticket,
                });
                this.setUserInfo(userInfo);
            }

            // 注销初始化
            const initialData = await revokeInit(channel);
            this.setInitialRevoke(initialData);

            // 是否需要展示条件和协议页面 0-不展示(直接跳至确认页面) 1-展示
            // prettier-ignore
            this.setTarget(
                initialData.showAcctDeletionAgreement
                    ? Target.RevokeCondition
                    : Target.Reconfirm
            );
        },
    },
};
</script>

<style lang="scss" scoped>
/deep/ .van-button {
    border-radius: var(--g-radius-md);
}

/deep/ h2 {
    font-weight: unset;
}
</style>
